/**
 * hostAuth.ts
 * ─────────────────────────────────────────────────────────────────────────────
 * Server-side host session verification.
 *
 * Replaces the localStorage-only `hostAuth === 'true'` check with a real
 * Supabase DB round-trip that confirms the hostId stored in localStorage
 * corresponds to a real, active, non-expired host account.
 *
 * Usage:
 *   const result = await verifyHostSession();
 *   if (!result.valid) { navigate('/host'); return; }
 *
 * The check is intentionally lightweight (single indexed PK lookup).
 * ─────────────────────────────────────────────────────────────────────────────
 */

import { supabase } from '@/integrations/supabase/client';

export type AccountType = 'venue_admin' | 'host';

export interface HostSessionResult {
  /** Whether the session is valid and the host is authorised to proceed. */
  valid: boolean;
  /** The host record when valid. */
  host?: {
    id: string;
    username: string;
    display_name: string;
    is_active: boolean;
    subscription_level: string;
    subscription_end_date: string | null;
    account_type: AccountType;
    venue_id: string | null;
  };
  /** Human-readable reason for rejection, useful for toast messages. */
  reason?: 'not_logged_in' | 'host_not_found' | 'inactive' | 'expired' | 'wrong_account_type';
}

/** Read the opaque server-issued session token from localStorage. */
export function getHostToken(): string | null {
  return localStorage.getItem('hostSessionToken');
}

/**
 * Verify the host session stored in localStorage against the database.
 *
 * The session is an opaque token issued by the server-side host_login() RPC;
 * verify_host_session() resolves it to a real, active host record. The token
 * (not the host id) is the credential, so a client can no longer impersonate a
 * host by setting a known UUID.
 *
 * Call this at the top of every host-protected page's useEffect.
 * Returns immediately without a network call when running in demo mode.
 */
export async function verifyHostSession(demo = false): Promise<HostSessionResult> {
  // Demo mode always passes — no real DB host exists for demo
  if (demo) return { valid: true };

  const token = getHostToken();

  // Fast-fail: no session token at all
  if (!token) {
    return { valid: false, reason: 'not_logged_in' };
  }

  try {
    const { data, error } = await (supabase as any)
      .rpc('verify_host_session', { p_token: token });

    if (error) {
      console.error('[hostAuth] Session verification error:', error);
      // Transport/DB error only — NOT an auth failure. An invalid or expired
      // token fails closed below (the `!row` branch clears the session). We stay
      // available here on a transient blip so a live show host isn't booted, and
      // it's safe to do so: every privileged action is independently gated by a
      // token-checked RPC server-side, so a rendered shell grants nothing.
      return { valid: true };
    }

    const row = (Array.isArray(data) ? data[0] : data) as {
      host_id: string; username: string; display_name: string;
      is_active: boolean; subscription_level: string;
      subscription_end_date: string | null;
      account_type: AccountType; venue_id: string | null;
    } | undefined;

    if (!row) {
      // Token invalid/expired — clear stale session
      clearHostSession();
      return { valid: false, reason: 'host_not_found' };
    }

    const host = {
      id: row.host_id,
      username: row.username,
      display_name: row.display_name,
      is_active: row.is_active,
      subscription_level: row.subscription_level,
      subscription_end_date: row.subscription_end_date,
      account_type: row.account_type,
      venue_id: row.venue_id,
    };

    if (host.is_active === false) {
      clearHostSession();
      return { valid: false, reason: 'inactive' };
    }

    if (host.subscription_end_date && new Date(host.subscription_end_date) <= new Date()) {
      // Expired — do NOT clear session; let the page handle the downgrade flow
      // (HostDashboard and HostShowMode already silently downgrade to 'basic')
      return {
        valid: true, // still valid — just on basic tier
        host: { ...host, is_active: true },
      };
    }

    return { valid: true, host };
  } catch (err) {
    console.error('[hostAuth] Session verification error:', err);
    // Transport error only (see the error branch above): stay available on a
    // transient failure; invalid tokens still fail closed, and server-side RPCs
    // remain the authoritative gate for every privileged action.
    return { valid: true };
  }
}

/**
 * Check that the stored session belongs to the expected account type.
 * Returns the session result unchanged — caller decides what to do on mismatch.
 */
export async function requireAccountType(
  expected: AccountType,
  demo = false,
): Promise<HostSessionResult> {
  const result = await verifyHostSession(demo);
  if (!result.valid) return result;
  if (result.host && result.host.account_type !== expected) {
    return { valid: false, reason: 'wrong_account_type' };
  }
  return result;
}

/** Read the account type from localStorage without a network call. */
export function getStoredAccountType(): AccountType | null {
  const t = localStorage.getItem('accountType');
  if (t === 'venue_admin' || t === 'host') return t;
  return null;
}

/**
 * Clear all host session tokens from localStorage and invalidate the token
 * server-side (best-effort). Call on logout or when the session is invalid.
 */
export function clearHostSession(): void {
  const token = localStorage.getItem('hostSessionToken');
  if (token) {
    // Fire-and-forget server-side invalidation.
    (supabase as any).rpc('host_logout', { p_token: token }).then?.(() => {}, () => {});
  }
  localStorage.removeItem('hostSessionToken');
  localStorage.removeItem('hostAuth');
  localStorage.removeItem('hostId');
  localStorage.removeItem('userRole');
  localStorage.removeItem('accountType');
}
